/**
 * OnlyFans CDN media in the React chat. Signed CDN links that are locked to
 * the Account's proxy IP (their CloudFront Policy names SourceIp/IpAddress)
 * must be fetched by the CRM through that proxy; anything else loads as is.
 * Same rules as the CRM's media-proxy.js / media-viewer.js.
 */
import { useEffect, useState } from 'react';
import { CRM_FRAME_PATH } from '../crm/crmBridge';
import { crmFetch, crmFetchRaw, dataOf } from './api';

const OF_CDN_HOST = /(^|\.)onlyfans\.com$/i;
const MAX_CONCURRENT = 4;
const BLOB_CACHE_MAX = 300;

function decodePolicy(policy: string): string {
  try {
    let b64 = policy.trim().replace(/-/g, '+').replace(/_/g, '=').replace(/~/g, '/');
    const pad = b64.length % 4;
    if (pad) b64 += '='.repeat(4 - pad);
    return atob(b64);
  } catch {
    return '';
  }
}

/** True when the URL is an OnlyFans CDN link only the Account's proxy can open. */
export function needsAccountProxy(url: string | null | undefined): boolean {
  const raw = String(url || '').trim();
  if (!/^https:\/\//i.test(raw)) return false;
  try {
    const u = new URL(raw);
    if (!OF_CDN_HOST.test(u.hostname)) return false;
    const policy = u.searchParams.get('Policy');
    return !!policy && /SourceIp|IpAddress/i.test(decodePolicy(policy));
  } catch {
    return true;
  }
}

// ── image bytes through the CRM, at most 4 at a time, cached by URL path ──
const blobUrls = new Map<string, string>();
const inflight = new Map<string, Promise<string>>();
const queue: Array<() => void> = [];
let active = 0;

function cacheKey(url: string): string {
  try {
    const u = new URL(url);
    return `${u.origin}${u.pathname}`;
  } catch {
    return url;
  }
}

function runQueued<T>(task: () => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const start = () => {
      active++;
      task()
        .then(resolve, reject)
        .finally(() => {
          active--;
          queue.shift()?.();
        });
    };
    if (active < MAX_CONCURRENT) start();
    else queue.push(start);
  });
}

function remember(key: string, objectUrl: string) {
  blobUrls.set(key, objectUrl);
  if (blobUrls.size > BLOB_CACHE_MAX) {
    const oldest = blobUrls.keys().next().value as string;
    URL.revokeObjectURL(blobUrls.get(oldest) as string);
    blobUrls.delete(oldest);
  }
}

/** An <img>-ready URL for `url` on this Account (an object URL when it had to be proxied). */
export function proxiedImageUrl(accountId: string, url: string): Promise<string> {
  if (!needsAccountProxy(url)) return Promise.resolve(url);
  const key = cacheKey(url);
  const hit = blobUrls.get(key);
  if (hit) return Promise.resolve(hit);
  const pending = inflight.get(key);
  if (pending) return pending;
  const p = runQueued(async () => {
    const res = await crmFetchRaw('/v1/media/cdn-fetch', { accountId, body: { url } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const objectUrl = URL.createObjectURL(await res.blob());
    remember(key, objectUrl);
    return objectUrl;
  }).finally(() => inflight.delete(key));
  inflight.set(key, p);
  return p;
}

export type MediaState = { url: string | null; failed: boolean };

/**
 * React hook: the displayable URL of a (possibly IP-locked) image. With `load`
 * false a proxied image is not fetched yet (e.g. its row is off screen); one
 * already fetched still shows at once.
 */
export function useMediaUrl(accountId: string, url: string | null | undefined, load = true): MediaState {
  const [state, setState] = useState<MediaState>(() => ({
    url: url && !needsAccountProxy(url) ? url : url ? blobUrls.get(cacheKey(url)) ?? null : null,
    failed: false,
  }));
  useEffect(() => {
    let alive = true;
    if (!url) {
      setState({ url: null, failed: false });
      return;
    }
    if (!load && needsAccountProxy(url) && !blobUrls.has(cacheKey(url))) {
      setState((cur) => (cur.url === null && !cur.failed ? cur : { url: null, failed: false }));
      return;
    }
    proxiedImageUrl(accountId, url).then(
      (u) => alive && setState((cur) => (cur.url === u && !cur.failed ? cur : { url: u, failed: false })),
      () => alive && setState({ url: null, failed: true }),
    );
    return () => {
      alive = false;
    };
  }, [accountId, url, load]);
  return state;
}

/**
 * A playable URL for video/audio: IP-locked links get a CRM stream session
 * (GET-able with Range, no auth header, valid ~10 min); others play directly.
 */
export async function playableUrl(accountId: string, fanId: string, url: string): Promise<string> {
  if (!needsAccountProxy(url)) return url;
  const body = await crmFetch('/v1/media/cdn-stream-session', { accountId, local: true, body: { url, fan_id: fanId } });
  const streamUrl = String(dataOf<{ stream_url?: string }>(body)?.stream_url || '');
  if (!streamUrl) throw new Error('No stream URL');
  return `${CRM_FRAME_PATH.replace(/\/$/, '')}${streamUrl}`;
}
